// @flow
import type { IntlShape } from 'react-intl';
import type { SuggestionType } from './types/suggestion';
import { VERTICAL_NAMES } from '../common/constants/common';
import suggestionsMessages from './messages';
import homesMessages from '../common/forms/messages.homes';
import productsMessages from '../common/forms/messages.products';

/**
 * It takes an array of suggestions and
 * returns the same array in a random order.
 */
export function shuffleSuggestions(suggestions: Array<SuggestionType>): ?Array<SuggestionType> {
  if (!Array.isArray(suggestions)) {
    return null;
  }

  if (suggestions.length === 0) {
    return suggestions;
  }

  let currentIndex = suggestions.length;
  let temporaryValue;
  let randomIndex;

  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;

    temporaryValue = suggestions[currentIndex];
    suggestions[currentIndex] = suggestions[randomIndex]; // eslint-disable-line no-param-reassign
    suggestions[randomIndex] = temporaryValue; // eslint-disable-line no-param-reassign
  }

  return suggestions;
}

/**
 * Compose the suggestion prefix translation.
 * If there is no typeId associated, returns a default prefix translation
 *
 * @example
 *   Given:
 *     - verticalName "homes"
 *     - typeId "venta"
 *   Returns:
 *     - "Viviendas en venta en"
 */
export function composeTranslatedSuggestionsPrefix(
  intl: IntlShape,
  verticalName: string,
  typeId: ?number
): string {
  const {
    suggestionsItemTitle,
    defaultProductsPrefix,
    defaultCarsPrefix,
    defaultJobsPrefix,
  } = suggestionsMessages;
  const EMPTY_PREFIX = '';

  switch (verticalName) {
    case VERTICAL_NAMES.homes: {
      if (!homesMessages[typeId]) {
        return EMPTY_PREFIX;
      }
      return intl.formatMessage(suggestionsItemTitle, {
        '%s': intl.formatMessage(homesMessages[typeId]),
      });
    }
    case VERTICAL_NAMES.cars: {
      return intl.formatMessage(suggestionsItemTitle, {
        '%s': intl.formatMessage(defaultCarsPrefix),
      });
    }
    case VERTICAL_NAMES.products: {
      const showDefaultMessage = typeId === 0 || !productsMessages[typeId];
      return intl.formatMessage(suggestionsItemTitle, {
        '%s': showDefaultMessage
          ? intl.formatMessage(defaultProductsPrefix)
          : intl.formatMessage(productsMessages[typeId]),
      });
    }
    case VERTICAL_NAMES.jobs: {
      return intl.formatMessage(suggestionsItemTitle, {
        '%s': intl.formatMessage(defaultJobsPrefix),
      });
    }
    default: {
      return EMPTY_PREFIX;
    }
  }
}



// WEBPACK FOOTER //
// ./app/Suggestions/utils.js